import { useMemo } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/i18n/LangContext";

/**
 * Editorial trial-state banner. Renders only when the user has a Lemon Squeezy
 * subscription. Adapts copy for:
 *  - on_trial  → days remaining until trial_ends_at
 *  - past_due  → urgent "update card" call
 *  - cancelled → soft "resume" call (still in current_period_end window)
 *  - expired   → muted post-mortem
 * Returns null when there is no subscription or status is plain "active".
 */
export default function TrialBanner() {
  const { user } = useAuth();
  const { t } = useLang();

  const view = useMemo(() => deriveView(user, t), [user, t]);
  if (!view) return null;

  const { tone, label, message, ctaLabel, ctaHref, testId } = view;
  const toneStyles =
    tone === "danger"
      ? "bg-[var(--oxblood)] text-[var(--bg)]"
      : tone === "muted"
        ? "bg-[var(--surface)] text-[var(--ink-soft)] border border-[var(--border)]"
        : "bg-[var(--ink)] text-[var(--bg)]";
  const accentLabel =
    tone === "danger"
      ? "text-[var(--gold)]"
      : tone === "muted"
        ? "text-[var(--oxblood)]"
        : "text-[var(--gold)]";

  return (
    <section
      className={`mt-10 px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${toneStyles}`}
      data-testid={testId}
    >
      <div className="flex-1">
        <p className={`label-cap ${accentLabel}`} data-testid="trial-banner-label">
          {label}
        </p>
        <p className="font-display-italic text-lg md:text-xl mt-2 leading-snug" data-testid="trial-banner-message">
          {message}
        </p>
      </div>
      {ctaHref && (
        <a
          href={ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="trial-banner-cta"
          className={`shrink-0 px-6 py-3 text-[12px] tracking-monocle uppercase transition-colors duration-400 ${
            tone === "danger"
              ? "bg-[var(--bg)] text-[var(--ink)] hover:bg-[var(--gold)]"
              : tone === "muted"
                ? "bg-[var(--ink)] text-[var(--bg)] hover:bg-[var(--oxblood)]"
                : "bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--bg)]"
          }`}
        >
          {ctaLabel}
        </a>
      )}
    </section>
  );
}

function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function fmt(template, n) {
  return template.replace("{n}", String(n));
}

function deriveView(user, t) {
  const sub = user?.subscription;
  if (!sub || !sub.status) return null;

  const b = t("app.trialBanner");
  const portalUrl = sub.customer_portal_url || sub.update_payment_method_url || null;

  if (sub.status === "on_trial") {
    const days = daysUntil(sub.trial_ends_at);
    if (days == null || days < 0) return null;
    let message;
    if (days === 0) message = `${b.endsToday} ${b.afterTrial}`;
    else if (days === 1) message = `${b.endsTomorrow} ${b.afterTrial}`;
    else message = `${fmt(b.endsInDays, days)} ${b.afterTrial}`;
    return {
      tone: "primary",
      label: b.labelTrial,
      message,
      ctaLabel: b.ctaManage,
      ctaHref: portalUrl,
      testId: "trial-banner-on-trial",
    };
  }

  if (sub.status === "past_due" || sub.status === "unpaid") {
    return {
      tone: "danger",
      label: b.labelGrace,
      message: b.pastDue,
      ctaLabel: b.ctaUpdate,
      ctaHref: sub.update_payment_method_url || sub.customer_portal_url || null,
      testId: "trial-banner-past-due",
    };
  }

  if (sub.status === "cancelled") {
    return {
      tone: "muted",
      label: b.labelEnded,
      message: b.cancelled,
      ctaLabel: b.ctaManage,
      ctaHref: portalUrl,
      testId: "trial-banner-cancelled",
    };
  }

  if (sub.status === "expired") {
    return {
      tone: "muted",
      label: b.labelEnded,
      message: b.expired,
      ctaLabel: b.ctaManage,
      ctaHref: portalUrl,
      testId: "trial-banner-expired",
    };
  }

  return null; // "active" → silent, no banner needed
}
